import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/_components/dialog";
import { useGetTransactionConversation } from "@/lib/api/hooks";
import { Loader2, MessageSquare, Send, Bot } from "lucide-react";

interface ConversationDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId: number;
}

const ConversationDetailsDialog = ({
  isOpen,
  onClose,
  transactionId,
}: ConversationDetailsDialogProps) => {
  const { data: conversationData, isLoading, error } = useGetTransactionConversation(
    transactionId,
    {
      query: {
        enabled: isOpen && !!transactionId,
      },
    }
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <MessageSquare className="h-5 w-5 text-primary" />
            Conversation Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2 text-primary" />
              <span className="text-muted-foreground">Loading conversation data...</span>
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-destructive">Failed to load conversation data</p>
            </div>
          )}

          {conversationData && conversationData.data && (
            <div className="space-y-6">
              {/* Message ID Section */}
              {conversationData.data.messageId && (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-primary" />
                    Message ID
                  </h3>
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                    <p className="text-sm text-foreground font-mono break-all">
                      {conversationData.data.messageId}
                    </p>
                  </div>
                </div>
              )}

              {/* Request Section */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Send className="h-4 w-4 text-blue-500" />
                  Request
                </h3>
                <div className="bg-muted/50 border border-border rounded-lg p-4">
                  <pre className="whitespace-pre-wrap text-sm text-foreground font-mono overflow-x-auto">
                    {conversationData.data.request}
                  </pre>
                </div>
              </div>

              {/* Response Section */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Bot className="h-4 w-4 text-green-500" />
                  Response
                </h3>
                <div className="bg-muted/50 border border-border rounded-lg p-4">
                  <pre className="whitespace-pre-wrap text-sm text-foreground font-mono overflow-x-auto">
                    {conversationData.data.response}
                  </pre>
                </div>
              </div>
            </div>
          )}

          {conversationData && !conversationData.data && (
            <div className="text-center py-8">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No Conversation Data</h3>
                <p className="text-muted-foreground">
                  No conversation data is available for this transaction.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConversationDetailsDialog;
